/* eslint-disable react/button-has-type */
import React, { ReactNode } from 'react'
import WLink from '../WLink/WLink'
import styles from './Button.module.sass'
import { Loader } from '@components/icons'

export interface Props {
  id?: string
  label?: string
  labelMobile?: string
  ariaLabel?: string
  type?: 'button' | 'submit'
  variant?: 'primary' | 'secondary' | 'ghost'
  theme?: 'light' | 'dark'
  disabled?: boolean
  icon?: ReactNode
  iconPos?: 'left' | 'right'
  size?: 'sm' | 'lg'
  responsive?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  onFocus?: (event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  href?: string
  newPage?: boolean
  className?: string
  loading?: boolean
  download?: boolean
  external?: boolean
  tabIndex?: number
}

const Button = (props: Props) => {
  const {
    id = '',
    label = '',
    labelMobile = undefined,
    ariaLabel = undefined,
    type = 'button',
    variant = 'primary',
    theme = 'light',
    disabled = false,
    icon = null,
    iconPos = 'left',
    size = 'lg',
    responsive = false,
    onClick = undefined,
    onFocus = undefined,
    href = null,
    newPage = false,
    className = '',
    loading = false,
    download = undefined,
    external = false,
    tabIndex = undefined,
  } = props

  const classeNames = `
    ${styles.root} 
    ${styles[`root--${variant}`]}
    ${styles[`root--${size}`]}
    ${styles[`root--${theme}`]}
    ${responsive ? styles[`root--responsive`] : ''}
    ${icon && !label && !labelMobile ? styles['root--no-label'] : ''}
    ${icon && !label && labelMobile ? styles['root--no-label-desktop'] : ''}
    ${icon && label && labelMobile === '' ? styles['root--no-label-mobile'] : ''}
    ${icon ? styles[`root--icon-${iconPos}`] : ''}
    ${loading ? styles['root--loading'] : ''}
    ${className ? className : ''}`

  const buttonContent = (
    <>
      {!!icon && <span className={styles.icon}>{icon}</span>}
      {!!label && (
        <span
          className={`${styles.label} ${
            typeof labelMobile !== 'undefined' ? styles['label--desktop'] : ''
          }`}
        >
          {label}
        </span>
      )}
      {!!labelMobile && (
        <span className={`${styles.label} ${styles['label--mobile']}`}>{labelMobile}</span>
      )}
      {loading && (
        <span className={styles.loader}>
          <Loader />
        </span>
      )}
    </>
  )

  return href ? (
    external ? (
      <a
        id={id}
        className={classeNames}
        href={href}
        target={newPage ? '_blank' : null}
        rel={`noreferrer ${newPage ? 'noopener' : ''}`}
        aria-label={ariaLabel}
        onClick={onClick}
        onFocus={onFocus}
        download={download || undefined}
      >
        {buttonContent}
      </a>
    ) : (
      <WLink
        href={href}
        onClick={onClick}
        onFocus={onFocus}
        className={classeNames}
        // rel={`noreferrer ${newPage ? 'noopener' : ''}`}
        newPage={newPage}
        ariaLabel={ariaLabel}
        download={download}
      >
        {buttonContent}
      </WLink>
    )
  ) : (
    <button
      id={id}
      type={type}
      className={classeNames}
      disabled={disabled}
      onClick={onClick}
      onFocus={onFocus}
      tabIndex={tabIndex || (onClick || type === 'submit' ? undefined : -1)}
      aria-label={ariaLabel}
    >
      {buttonContent}
    </button>
  )
}

export default Button
